import { isKgUnit, thicknessFromName } from './uom';
import { roundTo } from './quote-calc';

/**
 * CR-008 – Ductwork Calculator (ống gió tôn): hình học → diện tích → hao hụt → khối lượng → giá.
 * Hàm thuần, dùng chung API (nguồn sự thật) và giao diện (xem trước). Đặc tả: docs/phases/CR-008-ductwork.md.
 *
 * Nguyên tắc (prompt DUCTWORK_CALCULATOR_V1 của anh Tiến, 07/10/2026):
 * - 1 mã hàng (VD OGV075) dùng cho nhiều hình dạng; kích thước là dữ liệu giao dịch, không tạo mã theo kích thước.
 * - Mỗi loại có công thức riêng, ghi rõ độ chính xác: EXACT (công thức đóng), NUMERIC (tích phân số trên hình học thật,
 *   sai số < 1e-6 tương đối), APPROXIMATION (mô hình đơn giản hóa, ghi rõ giả định).
 * - Tính nội bộ đủ số lẻ (double), chỉ làm tròn ở đầu ra: m² 3 số lẻ, kg 2 số lẻ, tiền 0 số lẻ.
 * - Diện tích hình học là diện tích bề mặt ống, KHÔNG phải diện tích tôn triển khai (mép gấp, mối ghép nằm trong hao hụt).
 */

export const DUCT_AREA_DP = 3;
export const DUCT_WEIGHT_DP = 2;
/** Số khoảng tích phân Simpson (chẵn). 4096 khoảng cho sai số tương đối < 1e-9 với hàm trơn dùng ở đây. */
const SIMPSON_N = 4096;

export type DuctLengthUnit = 'mm' | 'cm' | 'm';
export type DuctFieldUnit = DuctLengthUnit | 'deg' | 'count' | 'kg';
export type DuctAccuracy = 'EXACT' | 'NUMERIC' | 'APPROXIMATION';
export type DuctMethod = 'GEOMETRY' | 'STANDARD_WEIGHT';
export type DuctFamily = 'rect' | 'round' | 'piece';
export const DUCT_PRICING_UNITS = ['M2', 'KG', 'PIECE'] as const;
export type DuctPricingUnit = (typeof DUCT_PRICING_UNITS)[number];
export const DUCT_PRICING_UNIT_LABELS: Record<DuctPricingUnit, string> = { M2: 'm²', KG: 'kg', PIECE: 'cái' };

/**
 * ĐVT trên báo giá (anh Tiến 08/10/2026): ống thẳng bán theo Ống, Mét hoặc m²; co, tê, phụ kiện khác theo Cái hoặc m².
 * Mặc định in Ống (ống thẳng) và Cái (phụ kiện). Giá vẫn nhập theo m² (hoặc kg); đơn giá dòng = thành tiền m² ÷ SL theo ĐVT chọn.
 * Dòng quy về m² qua hệ số riêng từng dòng (1 m² = hệ số × ĐVT dòng) → SL ĐVT chính (m²) vẫn đúng cho Asia/kho.
 */
export const DUCT_LINE_UNITS = ['PIPE', 'METER', 'M2', 'PIECE'] as const;
export type DuctLineUnit = (typeof DUCT_LINE_UNITS)[number];
export const DUCT_LINE_UNIT_LABELS: Record<DuctLineUnit, string> = { PIPE: 'Ống', METER: 'Mét', M2: 'm²', PIECE: 'Cái' };
const STRAIGHT_TYPES = new Set<string>(['rect_straight', 'round_straight']);

/** ĐVT dòng chọn được: ống thẳng Ống/Mét/m², phụ kiện Cái/m²; thành phẩm (miệng gió, louver, van) theo ĐVT chính của mã. */
export function ductLineUnits(r: Pick<DuctCalcResult, 'method' | 'calcType'>): DuctLineUnit[] {
  if (r.method !== 'GEOMETRY') return [];
  return STRAIGHT_TYPES.has(r.calcType) ? ['PIPE', 'METER', 'M2'] : ['PIECE', 'M2'];
}
/** Mặc định: ống thẳng = Ống, phụ kiện = Cái */
export const defaultDuctLineUnit = (calcType: DuctCalcType): DuctLineUnit => (STRAIGHT_TYPES.has(calcType) ? 'PIPE' : 'PIECE');

export const DUCT_CALC_TYPES = [
  'rect_straight',
  'rect_elbow',
  'rect_reducer',
  'rect_offset',
  'rect_tee',
  'rect_to_round',
  'rect_end_cap',
  'round_straight',
  'round_elbow',
  'round_reducer',
  'round_tee',
  'round_end_cap',
  'air_terminal',
  'louver',
  'damper',
] as const;
export type DuctCalcType = (typeof DUCT_CALC_TYPES)[number];

export interface DuctField {
  key: string;
  label: string;
  /** Đơn vị mặc định khi người dùng không ghi đơn vị */
  unit: DuctFieldUnit;
  required: boolean;
  default?: number;
  /** Cho phép = 0 (VD R trong của co vuông, cổ nối, lệch tâm) */
  allowZero?: boolean;
  /** Cho phép âm (lệch tâm theo chiều ngược) */
  allowNegative?: boolean;
  hint?: string;
}

export interface DuctTypeDef {
  type: DuctCalcType;
  label: string;
  family: DuctFamily;
  method: DuctMethod;
  accuracy: DuctAccuracy;
  /** Hao hụt mặc định (%) khi bảng hao hụt chưa cấu hình */
  defaultWastePercent: number;
  fields: DuctField[];
  /** Công thức tóm tắt (hiện cho người dùng) */
  formula: string;
  /** Cơ sở hình học / giả định */
  basis: string;
}

const mm = (key: string, label: string, extra: Partial<DuctField> = {}): DuctField => ({ key, label, unit: 'mm', required: true, ...extra });
const optMm = (key: string, label: string, hint?: string): DuctField => ({ key, label, unit: 'mm', required: false, default: 0, allowZero: true, hint });
const angle: DuctField = { key: 'angle', label: 'Góc', unit: 'deg', required: true, default: 90, hint: '0 < góc ≤ 180°' };
const collars = [optMm('collar1', 'Cổ nối đầu 1', 'Đoạn thẳng nối thêm ở đầu 1'), optMm('collar2', 'Cổ nối đầu 2', 'Đoạn thẳng nối thêm ở đầu 2')];
const pieceFields: DuctField[] = [
  mm('W', 'Rộng mặt (W)'),
  mm('H', 'Cao mặt (H)'),
  { key: 'kgPerPiece', label: 'Khối lượng chuẩn / cái', unit: 'kg', required: true, hint: 'kg/cái theo catalogue hoặc cân thực tế' },
];

export const DUCT_TYPES: Record<DuctCalcType, DuctTypeDef> = {
  rect_straight: {
    type: 'rect_straight',
    label: 'Ống thẳng vuông/chữ nhật',
    family: 'rect',
    method: 'GEOMETRY',
    accuracy: 'EXACT',
    defaultWastePercent: 10,
    fields: [mm('W', 'Rộng (W)'), mm('H', 'Cao (H)'), { key: 'L', label: 'Dài (L)', unit: 'm', required: true }],
    formula: 'A = 2 × (W + H) × L',
    basis: 'Chu vi tiết diện × chiều dài.',
  },
  rect_elbow: {
    type: 'rect_elbow',
    label: 'Co (cút) vuông/chữ nhật',
    family: 'rect',
    method: 'GEOMETRY',
    accuracy: 'EXACT',
    defaultWastePercent: 15,
    fields: [
      mm('W', 'Cạnh nằm trong mặt cong (W)', { hint: 'Co nằm: cạnh rộng; co đứng: cạnh cao' }),
      mm('H', 'Cạnh còn lại (H)'),
      { key: 'R', label: 'Bán kính trong (R)', unit: 'mm', required: true, allowZero: true, hint: 'Bán kính cung trong (họng); 0 = góc vuông trong' },
      angle,
      ...collars,
    ],
    formula: 'A = 2 × θ/2 × ((R+W)² − R²) + θ × (R+W) × H + θ × R × H + 2 × (W+H) × (cổ 1 + cổ 2)',
    basis: '2 má là hình vành khăn quạt góc θ; lưng (bán kính R+W) và bụng (bán kính R) là mặt trụ cao H.',
  },
  rect_reducer: {
    type: 'rect_reducer',
    label: 'Côn vuông/chữ nhật (đồng tâm, lệch tâm)',
    family: 'rect',
    method: 'GEOMETRY',
    accuracy: 'EXACT',
    defaultWastePercent: 12,
    fields: [
      mm('W1', 'Rộng đầu lớn (W1)'),
      mm('H1', 'Cao đầu lớn (H1)'),
      mm('W2', 'Rộng đầu nhỏ (W2)'),
      mm('H2', 'Cao đầu nhỏ (H2)'),
      mm('L', 'Dài côn (L)'),
      { key: 'eW', label: 'Lệch tâm theo W', unit: 'mm', required: false, default: 0, allowZero: true, allowNegative: true, hint: '0 = đồng tâm; phẳng 1 cạnh: (W1 − W2)/2' },
      { key: 'eH', label: 'Lệch tâm theo H', unit: 'mm', required: false, default: 0, allowZero: true, allowNegative: true, hint: '0 = đồng tâm; phẳng 1 cạnh: (H1 − H2)/2' },
      ...collars,
    ],
    formula: 'A = Σ 4 mặt hình thang: (a1 + a2)/2 × √(L² + Δ²) + 2 × (chu vi) × cổ',
    basis: 'Mỗi mặt nối 2 cạnh song song của 2 tiết diện nên là hình thang phẳng; Δ = độ dịch của cạnh theo phương vuông góc.',
  },
  rect_offset: {
    type: 'rect_offset',
    label: 'Lượn (offset) vuông/chữ nhật',
    family: 'rect',
    method: 'GEOMETRY',
    accuracy: 'APPROXIMATION',
    defaultWastePercent: 15,
    fields: [
      mm('W', 'Cạnh theo phương lệch (W)'),
      mm('H', 'Cạnh còn lại (H)'),
      mm('L', 'Dài lượn (L)'),
      { key: 'e', label: 'Độ lệch tâm (e)', unit: 'mm', required: true, allowZero: true },
      ...collars,
    ],
    formula: 'A ≈ 2 × W × L + 2 × H × √(L² + e²) + 2 × (W+H) × cổ',
    basis: 'GẦN ĐÚNG: mô hình lượn thẳng (2 mặt phẳng nghiêng), chưa tính đoạn cong chữ S.',
  },
  rect_tee: {
    type: 'rect_tee',
    label: 'Tê 90° vuông/chữ nhật',
    family: 'rect',
    method: 'GEOMETRY',
    accuracy: 'EXACT',
    defaultWastePercent: 17,
    fields: [
      mm('W', 'Rộng ống chính (W)', { hint: 'Mặt có nhánh' }),
      mm('H', 'Cao ống chính (H)'),
      mm('Lm', 'Dài ống chính (Lm)'),
      mm('Wb', 'Nhánh: cạnh dọc ống chính (Wb)', { hint: '≤ Lm' }),
      mm('Hb', 'Nhánh: cạnh ngang (Hb)', { hint: '≤ W' }),
      mm('Lb', 'Dài nhánh tính từ mặt ống chính (Lb)'),
    ],
    formula: 'A = 2 × (W+H) × Lm − Wb × Hb + 2 × (Wb+Hb) × Lb',
    basis: 'Ống chính trừ lỗ khoét nhánh, cộng thân nhánh vuông góc. Chạc Y, take-off, gót giầy làm ở bước 2.',
  },
  rect_to_round: {
    type: 'rect_to_round',
    label: 'Vuông tròn (chuyển tiếp)',
    family: 'rect',
    method: 'GEOMETRY',
    accuracy: 'NUMERIC',
    defaultWastePercent: 15,
    fields: [mm('W', 'Rộng đầu vuông (W)'), mm('H', 'Cao đầu vuông (H)'), mm('D', 'Đường kính đầu tròn (D)'), mm('L', 'Dài (L)'), ...collars],
    formula: 'A = 4 tam giác phẳng + 4 mặt nón góc (tích phân số) + cổ',
    basis: 'Phương pháp tam giác hóa (triangulation) chuẩn khi triển khai: mỗi cạnh vuông nối tới điểm tiếp tuyến giữa cạnh trên đường tròn, mỗi góc vuông nối tới 1/4 cung tròn. Đồng tâm.',
  },
  rect_end_cap: {
    type: 'rect_end_cap',
    label: 'Bịt đầu vuông/chữ nhật',
    family: 'rect',
    method: 'GEOMETRY',
    accuracy: 'EXACT',
    defaultWastePercent: 10,
    fields: [mm('W', 'Rộng (W)'), mm('H', 'Cao (H)'), optMm('collar1', 'Gờ/cổ bịt', 'Chiều sâu gờ bao quanh')],
    formula: 'A = W × H + 2 × (W+H) × gờ',
    basis: 'Mặt phẳng bịt + gờ bao.',
  },
  round_straight: {
    type: 'round_straight',
    label: 'Ống tròn thẳng',
    family: 'round',
    method: 'GEOMETRY',
    accuracy: 'EXACT',
    defaultWastePercent: 10,
    fields: [mm('D', 'Đường kính (D)'), { key: 'L', label: 'Dài (L)', unit: 'm', required: true }],
    formula: 'A = π × D × L',
    basis: 'Mặt trụ.',
  },
  round_elbow: {
    type: 'round_elbow',
    label: 'Co (cút) tròn',
    family: 'round',
    method: 'GEOMETRY',
    accuracy: 'EXACT',
    defaultWastePercent: 15,
    fields: [
      mm('D', 'Đường kính (D)'),
      { key: 'R', label: 'Bán kính tâm (R)', unit: 'mm', required: true, hint: 'Tính tới tâm ống, R ≥ D/2' },
      angle,
      { key: 'gores', label: 'Số múi', unit: 'count', required: false, default: 0, allowZero: true, hint: '0 = co trơn; số múi chỉ ảnh hưởng hao hụt' },
      ...collars,
    ],
    formula: 'A = π × D × R × θ + π × D × (cổ 1 + cổ 2)',
    basis: 'Định lý Pappus: diện tích mặt xuyến = chu vi tiết diện × đường đi của tâm (R × θ). Co nhiều múi: GẦN ĐÚNG (diện tích mặt trơn).',
  },
  round_reducer: {
    type: 'round_reducer',
    label: 'Côn tròn (đồng tâm, lệch tâm)',
    family: 'round',
    method: 'GEOMETRY',
    accuracy: 'EXACT',
    defaultWastePercent: 12,
    fields: [
      mm('D1', 'Đường kính đầu lớn (D1)'),
      mm('D2', 'Đường kính đầu nhỏ (D2)'),
      mm('L', 'Dài côn (L)'),
      { key: 'e', label: 'Lệch tâm (e)', unit: 'mm', required: false, default: 0, allowZero: true, hint: '0 = đồng tâm; phẳng 1 cạnh: (D1 − D2)/2' },
      ...collars,
    ],
    formula: 'Đồng tâm: S = √(L² + ((D1 − D2)/2)²), A = π × (D1 + D2)/2 × S. Lệch tâm: A = (r1+r2)/2 × ∫₀²π √(L² + (r1 − r2 − e·cosφ)²) dφ',
    basis: 'Mặt kẻ (ruled surface) nối 2 đường tròn theo cùng góc φ – đúng cách triển khai côn lệch tâm. e = 0 trùng công thức nón cụt. Lệch tâm tính tích phân số.',
  },
  round_tee: {
    type: 'round_tee',
    label: 'Tê 90° tròn',
    family: 'round',
    method: 'GEOMETRY',
    accuracy: 'NUMERIC',
    defaultWastePercent: 17,
    fields: [
      mm('Dm', 'Đường kính ống chính (Dm)'),
      mm('Lm', 'Dài ống chính (Lm)', { hint: '≥ Db' }),
      mm('Db', 'Đường kính nhánh (Db)', { hint: '≤ Dm' }),
      mm('Lb', 'Dài nhánh tính từ đỉnh ống chính (Lb)'),
    ],
    formula: 'A = π·Dm·Lm − lỗ khoét + thân nhánh (tích phân giao tuyến 2 trụ)',
    basis: 'Giao tuyến 2 mặt trụ vuông góc tính chính xác bằng tích phân số. Chạc Y, take-off, saddle làm ở bước 2.',
  },
  round_end_cap: {
    type: 'round_end_cap',
    label: 'Bịt đầu tròn',
    family: 'round',
    method: 'GEOMETRY',
    accuracy: 'EXACT',
    defaultWastePercent: 10,
    fields: [mm('D', 'Đường kính (D)'), optMm('collar1', 'Gờ/cổ bịt', 'Chiều sâu gờ bao quanh')],
    formula: 'A = π × D²/4 + π × D × gờ',
    basis: 'Mặt tròn bịt + gờ bao.',
  },
  air_terminal: {
    type: 'air_terminal',
    label: 'Miệng gió / diffuser',
    family: 'piece',
    method: 'STANDARD_WEIGHT',
    accuracy: 'EXACT',
    defaultWastePercent: 0,
    fields: pieceFields,
    formula: 'Khối lượng = SL × kg chuẩn/cái; diện tích mặt = W × H (chỉ để tham khảo/tính giá theo m² mặt)',
    basis: 'Thành phẩm lắp ráp: không lấy m² × dày × khối lượng riêng.',
  },
  louver: {
    type: 'louver',
    label: 'Louver',
    family: 'piece',
    method: 'STANDARD_WEIGHT',
    accuracy: 'EXACT',
    defaultWastePercent: 0,
    fields: pieceFields,
    formula: 'Khối lượng = SL × kg chuẩn/cái; diện tích mặt (gross face) = W × H',
    basis: 'Gross face area ≠ diện tích vật tư. Tính theo cấu tạo (khung, lá, lưới) làm ở bước 2.',
  },
  damper: {
    type: 'damper',
    label: 'Van gió (OBD/VCD/FD/FSD…)',
    family: 'piece',
    method: 'STANDARD_WEIGHT',
    accuracy: 'EXACT',
    defaultWastePercent: 0,
    fields: pieceFields,
    formula: 'Khối lượng = SL × kg chuẩn/cái; diện tích mặt = W × H',
    basis: 'Thành phẩm: không lấy diện tích mặt trước làm khối lượng. Tính theo cấu tạo làm ở bước 2.',
  },
};

// ---------------------------------------------------------------------------
// Vật liệu (MATERIAL ENGINE) – mặc định; máy chủ đọc bảng ductwork_materials (sửa được)
// ---------------------------------------------------------------------------

export interface DuctMaterial {
  code: string;
  name: string;
  /** kg/m³ */
  densityKgM3: number;
}

export const DEFAULT_DUCT_MATERIALS: DuctMaterial[] = [
  { code: 'GI', name: 'Tôn mạ kẽm (GI)', densityKgM3: 7850 },
  { code: 'STEEL', name: 'Thép đen', densityKgM3: 7850 },
  { code: 'SS304', name: 'Inox 304', densityKgM3: 7930 },
  { code: 'SS316', name: 'Inox 316', densityKgM3: 8000 },
  { code: 'AL', name: 'Nhôm', densityKgM3: 2700 },
];

/** kg/m² = dày (mm) × khối lượng riêng (kg/m³) / 1000. VD GI 0,75 mm: 5,8875 kg/m². */
export const kgPerM2 = (thicknessMm: number, densityKgM3: number) => (thicknessMm * densityKgM3) / 1000;

// ---------------------------------------------------------------------------
// Hồ sơ mã hàng (PRODUCT MASTER → calculator): mã là master data, kích thước là giao dịch
// ---------------------------------------------------------------------------

export interface DuctProductProfile {
  /** Nhóm loại tính cho mã; any = cả vuông và tròn */
  family: 'rect' | 'round' | 'any' | 'piece';
  pieceType: 'air_terminal' | 'louver' | 'damper' | null;
  materialCode: string | null;
  thicknessMm: number | null;
  /** Mã ống gió chống cháy (tên có "EI30/EI45/…") → hộp tính có thêm lớp tấm EI */
  ei?: boolean;
}

const isM2Unit = (u: string | null | undefined) => !!u && /^(m2|m²|mét vuông|met vuong)$/i.test(u.trim());
export { isM2Unit as isM2DuctUnit };

/**
 * Suy hồ sơ calculator từ mã: tên "Ống gió vuông/tròn/EI…" ĐVT m² → ống; miệng gió/louver/van → thành phẩm.
 * Không suy từ mã hàng (OGV075 không chắc luôn chứa độ dày) – chỉ từ tên, cột độ dày, ĐVT. null = không phải hàng ống gió.
 */
export function ductProfileOf(p: { name?: string | null; unit?: string | null; uoms?: { uom: string }[] | null; thicknessMm?: number | string | null }): DuctProductProfile | null {
  const name = (p.name ?? '').normalize('NFC');
  const material = /inox|sus|\b304\b|\b316\b/i.test(name) ? (/316/.test(name) ? 'SS316' : 'SS304') : /nhôm|nhom|alu/i.test(name) ? 'AL' : 'GI';
  const col = p.thicknessMm == null ? 0 : Number(p.thicknessMm);
  // "Ống Gió EI30-45 (0.75mm)": độ dày nằm trong ngoặc
  const thicknessMm = col > 0 ? col : (thicknessFromName(name) ?? thicknessFromName(name.replace(/[()]/g, ' ')));
  const hasM2 = isM2Unit(p.unit) || (p.uoms ?? []).some((u) => isM2Unit(u.uom));
  if (/ống\s*gió|ong\s*gio/i.test(name) && hasM2 && !/mềm|mem/i.test(name)) {
    const family = /vuông|chữ nhật|vuong/i.test(name) ? 'rect' : /tròn|tron/i.test(name) ? 'round' : 'any';
    return { family, pieceType: null, materialCode: material, thicknessMm, ei: /\bEI\s*\d{2,3}/i.test(name) };
  }
  const pieceType = /miệng\s*gió|diffuser|grille|cửa\s*gió/i.test(name)
    ? 'air_terminal'
    : /louver/i.test(name)
      ? 'louver'
      : /\b(obd|vcd|fd|fsd)\b|van\s*(gió|chặn\s*lửa|chặn\s*khói)|damper/i.test(name)
        ? 'damper'
        : null;
  return pieceType ? { family: 'piece', pieceType, materialCode: material, thicknessMm } : null;
}

/** Các loại tính chọn được cho mã. */
export function ductTypesFor(profile: DuctProductProfile | null): DuctCalcType[] {
  if (!profile) return [];
  if (profile.family === 'piece') return profile.pieceType ? [profile.pieceType] : [];
  return DUCT_CALC_TYPES.filter((t) => {
    const f = DUCT_TYPES[t].family;
    if (f === 'piece') return false;
    return profile.family === 'any' || f === profile.family || (t === 'rect_to_round' && profile.family === 'round');
  });
}

// ---------------------------------------------------------------------------
// Đầu vào / kết quả
// ---------------------------------------------------------------------------

export type DuctDimValue = number | { value: number; unit?: DuctLengthUnit };

export interface DuctCalcInput {
  calcType: DuctCalcType;
  dimensions: Record<string, DuctDimValue | null | undefined>;
  /** Số cái (Quantity) */
  pieces: number;
  /** Bỏ trống = hao hụt mặc định của loại */
  wastePercent?: number | null;
  material?: DuctMaterial | null;
  thicknessMm?: number | null;
}

export interface DuctStep {
  key: string;
  label: string;
  formula: string;
  value: number;
  unit: string;
}

export interface DuctCalcResult {
  calcType: DuctCalcType;
  label: string;
  method: DuctMethod;
  accuracy: DuctAccuracy;
  formula: string;
  basis: string;
  /** Kích thước đã chuẩn hóa: chiều dài theo mm (L ống thẳng cũng đổi ra mm), góc theo độ */
  dimensionsMm: Record<string, number>;
  /** Mô tả kích thước để in: "D500 × R750 × 90°" */
  dimensionText: string;
  pieces: number;
  netAreaPerPieceM2: number | null;
  netAreaM2: number | null;
  wastePercent: number;
  wasteAreaM2: number | null;
  totalAreaM2: number | null;
  /** Diện tích mặt (gross face) – chỉ thành phẩm; KHÔNG phải diện tích vật tư */
  faceAreaPerPieceM2: number | null;
  materialCode: string | null;
  materialName: string | null;
  densityKgM3: number | null;
  thicknessMm: number | null;
  kgPerM2: number | null;
  kgPerPiece: number | null;
  totalWeightKg: number | null;
  steps: DuctStep[];
  warnings: string[];
}

export class DuctworkError extends Error {
  constructor(
    message: string,
    public field?: string,
  ) {
    super(message);
  }
}

const TO_MM: Record<DuctLengthUnit, number> = { mm: 1, cm: 10, m: 1000 };

function readDims(def: DuctTypeDef, dims: DuctCalcInput['dimensions']): Record<string, number> {
  const out: Record<string, number> = {};
  for (const f of def.fields) {
    const raw = dims?.[f.key];
    let v: number;
    let unit: DuctFieldUnit = f.unit;
    if (raw === null || raw === undefined || (typeof raw === 'object' && (raw.value === null || raw.value === undefined))) {
      if (f.required) throw new DuctworkError(`Nhập ${f.label}`, f.key);
      v = f.default ?? 0;
    } else if (typeof raw === 'object') {
      v = Number(raw.value);
      if (raw.unit) {
        if (!(raw.unit in TO_MM) || (f.unit !== 'mm' && f.unit !== 'cm' && f.unit !== 'm')) throw new DuctworkError(`${f.label}: đơn vị không hợp lệ`, f.key);
        unit = raw.unit;
      }
    } else v = Number(raw);
    if (!Number.isFinite(v)) throw new DuctworkError(`${f.label} không hợp lệ`, f.key);
    if (v < 0 && !f.allowNegative) throw new DuctworkError(`${f.label} không được âm`, f.key);
    if (v === 0 && f.required && !f.allowZero) throw new DuctworkError(`${f.label} phải lớn hơn 0`, f.key);
    if (f.unit === 'count' && !Number.isInteger(v)) throw new DuctworkError(`${f.label} phải là số nguyên`, f.key);
    // Chuẩn hóa chiều dài về mm; góc, số múi, kg giữ nguyên
    out[f.key] = unit === 'mm' || unit === 'cm' || unit === 'm' ? v * TO_MM[unit] : v;
  }
  return out;
}

/** Simpson tổng hợp trên [a, b]. */
function simpson(f: (x: number) => number, a: number, b: number, n = SIMPSON_N): number {
  const h = (b - a) / n;
  let s = f(a) + f(b);
  for (let i = 1; i < n; i++) s += f(a + i * h) * (i % 2 ? 4 : 2);
  return (s * h) / 3;
}

const rad = (deg: number) => (deg * Math.PI) / 180;
/** Số trong chuỗi công thức: 6 số lẻ, bỏ số 0 thừa (chỉ để hiển thị, không dùng để tính). */
const fmt = (v: number) => String(Math.round(v * 1e6) / 1e6);

/** Diện tích mặt tam giác 3 điểm 3D. */
function triArea(a: number[], b: number[], c: number[]) {
  const u = [b[0]! - a[0]!, b[1]! - a[1]!, b[2]! - a[2]!];
  const v = [c[0]! - a[0]!, c[1]! - a[1]!, c[2]! - a[2]!];
  const x = u[1]! * v[2]! - u[2]! * v[1]!;
  const y = u[2]! * v[0]! - u[0]! * v[2]!;
  const z = u[0]! * v[1]! - u[1]! * v[0]!;
  return Math.sqrt(x * x + y * y + z * z) / 2;
}

/** Diện tích hình học 1 cái (m²) + các bước trung gian. Kích thước vào theo mm. */
function geometry(type: DuctCalcType, d: Record<string, number>, steps: DuctStep[], warnings: string[]): number {
  const m = (k: string) => d[k]! / 1000;
  const push = (key: string, label: string, formula: string, value: number, unit = 'm²') => steps.push({ key, label, formula, value, unit });
  const collarRect = (w: number, h: number) => {
    const c = (d.collar1 ?? 0) / 1000 + (d.collar2 ?? 0) / 1000;
    const a = 2 * (w + h) * c;
    if (c > 0) push('collar', 'Cổ nối', `2 × (${fmt(w)} + ${fmt(h)}) × ${fmt(c)}`, a);
    return a;
  };
  const collarRound = (dia: number) => {
    const c = (d.collar1 ?? 0) / 1000 + (d.collar2 ?? 0) / 1000;
    const a = Math.PI * dia * c;
    if (c > 0) push('collar', 'Cổ nối', `π × ${fmt(dia)} × ${fmt(c)}`, a);
    return a;
  };
  const checkAngle = () => {
    if (!(d.angle! > 0 && d.angle! <= 180)) throw new DuctworkError('Góc phải trong khoảng 0 < góc ≤ 180°', 'angle');
    return rad(d.angle!);
  };

  switch (type) {
    case 'rect_straight': {
      const [W, H, L] = [m('W'), m('H'), m('L')];
      const a = 2 * (W + H) * L;
      push('net', 'Diện tích ống', `2 × (${fmt(W)} + ${fmt(H)}) × ${fmt(L)}`, a);
      return a;
    }
    case 'rect_elbow': {
      const [W, H, R] = [m('W'), m('H'), m('R')];
      const t = checkAngle();
      const cheeks = 2 * (t / 2) * ((R + W) ** 2 - R ** 2);
      const heel = t * (R + W) * H;
      const throat = t * R * H;
      push('cheeks', '2 má (vành khăn)', `2 × ${fmt(t)}/2 × ((${fmt(R)} + ${fmt(W)})² − ${fmt(R)}²)`, cheeks);
      push('heel', 'Lưng (cung ngoài)', `${fmt(t)} × ${fmt(R + W)} × ${fmt(H)}`, heel);
      push('throat', 'Bụng (cung trong)', `${fmt(t)} × ${fmt(R)} × ${fmt(H)}`, throat);
      return cheeks + heel + throat + collarRect(W, H);
    }
    case 'rect_reducer':
    case 'rect_offset': {
      const off = type === 'rect_offset';
      const W1 = m(off ? 'W' : 'W1');
      const H1 = m(off ? 'H' : 'H1');
      const W2 = m(off ? 'W' : 'W2');
      const H2 = m(off ? 'H' : 'H2');
      const L = m('L');
      const eW = (off ? d.e! : (d.eW ?? 0)) / 1000;
      const eH = off ? 0 : (d.eH ?? 0) / 1000;
      // Mặt trên/dưới (cạnh song song phương W, dịch theo phương H), mặt trái/phải (song song phương H, dịch theo W)
      const face = (a1: number, a2: number, shift: number) => ((a1 + a2) / 2) * Math.sqrt(L * L + shift * shift);
      const top = face(W1, W2, eH + H2 / 2 - H1 / 2);
      const bottom = face(W1, W2, eH - H2 / 2 + H1 / 2);
      const right = face(H1, H2, eW + W2 / 2 - W1 / 2);
      const left = face(H1, H2, eW - W2 / 2 + W1 / 2);
      push('faces_wh', 'Mặt trên + dưới', `(${fmt(W1)} + ${fmt(W2)})/2 × √(L² + Δ²) × 2 mặt`, top + bottom);
      push('faces_h', 'Mặt trái + phải', `(${fmt(H1)} + ${fmt(H2)})/2 × √(L² + Δ²) × 2 mặt`, left + right);
      const collars = (d.collar1 ?? 0) / 1000 * 2 * (W1 + H1) + (d.collar2 ?? 0) / 1000 * 2 * (W2 + H2);
      if (collars > 0) push('collar', 'Cổ nối', '2 × (W1+H1) × cổ 1 + 2 × (W2+H2) × cổ 2', collars);
      if (off) warnings.push('Lượn tính GẦN ĐÚNG theo mô hình 2 mặt phẳng nghiêng, chưa tính đoạn cong chữ S.');
      return top + bottom + right + left + collars;
    }
    case 'rect_tee': {
      const [W, H, Lm, Wb, Hb, Lb] = [m('W'), m('H'), m('Lm'), m('Wb'), m('Hb'), m('Lb')];
      if (Wb > Lm) throw new DuctworkError('Cạnh nhánh dọc ống chính (Wb) không được lớn hơn dài ống chính (Lm)', 'Wb');
      if (Hb > W) throw new DuctworkError('Cạnh nhánh ngang (Hb) không được lớn hơn rộng ống chính (W)', 'Hb');
      const main = 2 * (W + H) * Lm;
      const hole = Wb * Hb;
      const branch = 2 * (Wb + Hb) * Lb;
      push('main', 'Ống chính', `2 × (${fmt(W)} + ${fmt(H)}) × ${fmt(Lm)}`, main);
      push('hole', 'Trừ lỗ khoét nhánh', `${fmt(Wb)} × ${fmt(Hb)}`, -hole);
      push('branch', 'Thân nhánh', `2 × (${fmt(Wb)} + ${fmt(Hb)}) × ${fmt(Lb)}`, branch);
      return main - hole + branch;
    }
    case 'rect_to_round': {
      const [W, H, D, L] = [m('W'), m('H'), m('D'), m('L')];
      const r = D / 2;
      // Đầu vuông ở z = 0, đầu tròn ở z = L, đồng tâm. Điểm tiếp tuyến trên đường tròn ở góc 0, 90, 180, 270°.
      const corners = [
        [W / 2, H / 2, 0],
        [-W / 2, H / 2, 0],
        [-W / 2, -H / 2, 0],
        [W / 2, -H / 2, 0],
      ];
      const circ = (phi: number) => [r * Math.cos(phi), r * Math.sin(phi), L];
      // Cạnh i nối góc i → góc i+1, tiếp tuyến tại góc pháp tuyến của cạnh: trên (90°), trái (180°), dưới (270°), phải (0°)
      const sideNormal = [Math.PI / 2, Math.PI, (3 * Math.PI) / 2, 0];
      let tris = 0;
      for (let i = 0; i < 4; i++) tris += triArea(corners[i]!, corners[(i + 1) % 4]!, circ(sideNormal[i]!));
      // Góc i nằm giữa cạnh i−1 và cạnh i (VD góc (+,+) giữa cạnh phải 0° và cạnh trên 90°) → nối tới 1/4 cung giữa 2 điểm tiếp tuyến
      let cones = 0;
      const N = SIMPSON_N;
      for (let i = 0; i < 4; i++) {
        const from = sideNormal[(i + 3) % 4]!;
        let to = sideNormal[i]!;
        if (to < from) to += 2 * Math.PI;
        for (let k = 0; k < N; k++) cones += triArea(corners[i]!, circ(from + ((to - from) * k) / N), circ(from + ((to - from) * (k + 1)) / N));
      }
      push('triangles', '4 tam giác phẳng', 'Σ tam giác (cạnh vuông → điểm tiếp tuyến)', tris);
      push('cones', '4 mặt nón góc', `Σ ${N} tam giác nhỏ / góc (góc vuông → 1/4 cung)`, cones);
      const collars = (d.collar1 ?? 0) / 1000 * 2 * (W + H) + (d.collar2 ?? 0) / 1000 * Math.PI * D;
      if (collars > 0) push('collar', 'Cổ nối', '2 × (W+H) × cổ 1 + π × D × cổ 2', collars);
      return tris + cones + collars;
    }
    case 'rect_end_cap': {
      const [W, H] = [m('W'), m('H')];
      const a = W * H;
      push('plate', 'Mặt bịt', `${fmt(W)} × ${fmt(H)}`, a);
      return a + collarRect(W, H);
    }
    case 'round_straight': {
      const [D, L] = [m('D'), m('L')];
      const a = Math.PI * D * L;
      push('net', 'Diện tích ống', `π × ${fmt(D)} × ${fmt(L)}`, a);
      return a;
    }
    case 'round_elbow': {
      const [D, R] = [m('D'), m('R')];
      const t = checkAngle();
      if (R < D / 2) throw new DuctworkError('Bán kính tâm R phải ≥ D/2', 'R');
      const arc = R * t;
      push('arc', 'Chiều dài cung tâm', `${fmt(R)} × ${fmt(t)} rad`, arc, 'm');
      const a = Math.PI * D * arc;
      push('net', 'Mặt cong (Pappus)', `π × ${fmt(D)} × ${fmt(arc)}`, a);
      if ((d.gores ?? 0) > 0) warnings.push(`Co ${d.gores} múi: diện tích lấy theo mặt cong trơn (GẦN ĐÚNG); múi, mép ghép tính trong hao hụt.`);
      return a + collarRound(D);
    }
    case 'round_reducer': {
      const [D1, D2, L] = [m('D1'), m('D2'), m('L')];
      const e = (d.e ?? 0) / 1000;
      const [r1, r2] = [D1 / 2, D2 / 2];
      let a: number;
      if (e === 0) {
        const S = Math.sqrt(L * L + ((D1 - D2) / 2) ** 2);
        push('slant', 'Đường sinh S', `√(${fmt(L)}² + ((${fmt(D1)} − ${fmt(D2)})/2)²)`, S, 'm');
        a = Math.PI * ((D1 + D2) / 2) * S;
        push('net', 'Mặt bên nón cụt', `π × (${fmt(D1)} + ${fmt(D2)})/2 × ${fmt(S)}`, a);
      } else {
        const dr = r1 - r2;
        const integral = simpson((phi) => Math.sqrt(L * L + (dr - e * Math.cos(phi)) ** 2), 0, 2 * Math.PI);
        a = ((r1 + r2) / 2) * integral;
        push('integral', '∫₀²π √(L² + (r1 − r2 − e·cosφ)²) dφ', `Simpson ${SIMPSON_N} khoảng`, integral, 'm');
        push('net', 'Mặt bên côn lệch tâm', `(${fmt(r1)} + ${fmt(r2)})/2 × ${fmt(integral)}`, a);
      }
      const collars = (d.collar1 ?? 0) / 1000 * Math.PI * D1 + (d.collar2 ?? 0) / 1000 * Math.PI * D2;
      if (collars > 0) push('collar', 'Cổ nối', 'π × D1 × cổ 1 + π × D2 × cổ 2', collars);
      return a + collars;
    }
    case 'round_tee': {
      const [Dm, Lm, Db, Lb] = [m('Dm'), m('Lm'), m('Db'), m('Lb')];
      if (Db > Dm) throw new DuctworkError('Đường kính nhánh (Db) không được lớn hơn ống chính (Dm)', 'Db');
      if (Lm < Db) throw new DuctworkError('Dài ống chính (Lm) phải ≥ đường kính nhánh (Db)', 'Lm');
      const [Rm, Rb] = [Dm / 2, Db / 2];
      const main = Math.PI * Dm * Lm;
      // Lỗ khoét trên mặt trụ chính: Rm × ∫ 2√(Rb² − Rm²cos²ψ) dψ trên miền Rm|cosψ| ≤ Rb, sinψ > 0
      const psi0 = Math.acos(Math.min(1, Rb / Rm));
      const hole = Rm * simpson((psi) => 2 * Math.sqrt(Math.max(0, Rb * Rb - Rm * Rm * Math.cos(psi) ** 2)), psi0, Math.PI - psi0);
      // Thân nhánh: từ giao tuyến z = √(Rm² − Rb²sin²φ) tới đầu nhánh z = Rm + Lb
      const branch = Rb * simpson((phi) => Rm + Lb - Math.sqrt(Rm * Rm - Rb * Rb * Math.sin(phi) ** 2), 0, 2 * Math.PI);
      push('main', 'Ống chính', `π × ${fmt(Dm)} × ${fmt(Lm)}`, main);
      push('hole', 'Trừ lỗ khoét (tích phân)', 'Rm × ∫ 2√(Rb² − Rm²cos²ψ) dψ', -hole);
      push('branch', 'Thân nhánh tới giao tuyến (tích phân)', 'Rb × ∫₀²π (Rm + Lb − √(Rm² − Rb²sin²φ)) dφ', branch);
      return main - hole + branch;
    }
    case 'round_end_cap': {
      const D = m('D');
      const a = (Math.PI * D * D) / 4;
      push('plate', 'Mặt bịt', `π × ${fmt(D)}² / 4`, a);
      const c = (d.collar1 ?? 0) / 1000;
      const ca = Math.PI * D * c;
      if (c > 0) push('collar', 'Gờ bịt', `π × ${fmt(D)} × ${fmt(c)}`, ca);
      return a + ca;
    }
    default:
      throw new DuctworkError('Loại này không tính theo hình học');
  }
}

function dimensionText(type: DuctCalcType, d: Record<string, number>): string {
  const n = (v: number) => fmt(v);
  switch (type) {
    case 'rect_straight':
      return `${n(d.W!)}×${n(d.H!)} L=${n(d.L! / 1000)}m`;
    case 'rect_elbow':
      return `${n(d.W!)}×${n(d.H!)} R${n(d.R!)} ${n(d.angle!)}°`;
    case 'rect_reducer':
      return `${n(d.W1!)}×${n(d.H1!)}/${n(d.W2!)}×${n(d.H2!)} L=${n(d.L!)}${d.eW || d.eH ? ` lệch ${n(d.eW ?? 0)}/${n(d.eH ?? 0)}` : ''}`;
    case 'rect_offset':
      return `${n(d.W!)}×${n(d.H!)} L=${n(d.L!)} lệch ${n(d.e!)}`;
    case 'rect_tee':
      return `${n(d.W!)}×${n(d.H!)} L=${n(d.Lm!)} / nhánh ${n(d.Wb!)}×${n(d.Hb!)} L=${n(d.Lb!)}`;
    case 'rect_to_round':
      return `${n(d.W!)}×${n(d.H!)}/D${n(d.D!)} L=${n(d.L!)}`;
    case 'rect_end_cap':
      return `${n(d.W!)}×${n(d.H!)}`;
    case 'round_straight':
      return `D${n(d.D!)} L=${n(d.L! / 1000)}m`;
    case 'round_elbow':
      return `D${n(d.D!)} R${n(d.R!)} ${n(d.angle!)}°${d.gores ? ` ${d.gores} múi` : ''}`;
    case 'round_reducer':
      return `D${n(d.D1!)}/D${n(d.D2!)} L=${n(d.L!)}${d.e ? ` lệch ${n(d.e)}` : ''}`;
    case 'round_tee':
      return `D${n(d.Dm!)} L=${n(d.Lm!)} / nhánh D${n(d.Db!)} L=${n(d.Lb!)}`;
    case 'round_end_cap':
      return `D${n(d.D!)}`;
    default:
      return `${n(d.W!)}×${n(d.H!)}`;
  }
}

/**
 * CALCULATION + WASTE + WEIGHT ENGINE: kích thước thực tế → diện tích → hao hụt → khối lượng.
 * Không làm tròn giữa các bước; kết quả trả đủ số lẻ, làm tròn khi hiển thị/lưu (ductRounded).
 */
export function calcDuctwork(input: DuctCalcInput): DuctCalcResult {
  const def = DUCT_TYPES[input.calcType];
  if (!def) throw new DuctworkError('Loại tính không hợp lệ', 'calcType');
  const pieces = Number(input.pieces);
  if (!Number.isInteger(pieces) || pieces <= 0) throw new DuctworkError('Số lượng (cái) phải là số nguyên lớn hơn 0', 'pieces');
  const waste = input.wastePercent === null || input.wastePercent === undefined ? def.defaultWastePercent : Number(input.wastePercent);
  if (!Number.isFinite(waste) || waste < 0 || waste > 100) throw new DuctworkError('Hao hụt phải từ 0 đến 100%', 'wastePercent');
  const d = readDims(def, input.dimensions);
  const steps: DuctStep[] = [];
  const warnings: string[] = [];
  const material = input.material ?? null;
  const thickness = input.thicknessMm === null || input.thicknessMm === undefined ? null : Number(input.thicknessMm);

  if (def.method === 'STANDARD_WEIGHT') {
    const kgPerPiece = d.kgPerPiece!;
    if (!(kgPerPiece > 0)) throw new DuctworkError('Khối lượng chuẩn / cái phải lớn hơn 0', 'kgPerPiece');
    const face = (d.W! / 1000) * (d.H! / 1000);
    steps.push({ key: 'face', label: 'Diện tích mặt (gross face, không phải vật tư)', formula: `${fmt(d.W! / 1000)} × ${fmt(d.H! / 1000)}`, value: face, unit: 'm²' });
    const weight = kgPerPiece * pieces;
    steps.push({ key: 'weight', label: 'Khối lượng', formula: `${pieces} cái × ${fmt(kgPerPiece)} kg/cái`, value: weight, unit: 'kg' });
    return {
      calcType: def.type,
      label: def.label,
      method: def.method,
      accuracy: def.accuracy,
      formula: def.formula,
      basis: def.basis,
      dimensionsMm: d,
      dimensionText: dimensionText(def.type, d),
      pieces,
      netAreaPerPieceM2: null,
      netAreaM2: null,
      wastePercent: 0,
      wasteAreaM2: null,
      totalAreaM2: null,
      faceAreaPerPieceM2: face,
      materialCode: material?.code ?? null,
      materialName: material?.name ?? null,
      densityKgM3: material?.densityKgM3 ?? null,
      thicknessMm: thickness,
      kgPerM2: null,
      kgPerPiece,
      totalWeightKg: weight,
      steps,
      warnings,
    };
  }

  if (!material) throw new DuctworkError('Chọn vật liệu', 'material');
  if (!(material.densityKgM3 > 0)) throw new DuctworkError('Khối lượng riêng vật liệu phải lớn hơn 0', 'material');
  if (thickness === null || !(thickness > 0)) throw new DuctworkError('Nhập độ dày tôn (mm) lớn hơn 0', 'thicknessMm');

  const perPiece = geometry(def.type, d, steps, warnings);
  if (!(perPiece > 0)) throw new DuctworkError('Diện tích tính ra không hợp lệ, kiểm tra lại kích thước');
  const net = perPiece * pieces;
  steps.push({ key: 'net_total', label: 'Diện tích thực', formula: `${fmt(perPiece)} m²/cái × ${pieces} cái`, value: net, unit: 'm²' });
  const wasteArea = (net * waste) / 100;
  steps.push({ key: 'waste', label: 'Hao hụt', formula: `${fmt(net)} × ${waste}%`, value: wasteArea, unit: 'm²' });
  const total = net + wasteArea;
  steps.push({ key: 'total', label: 'Diện tích vật tư', formula: `${fmt(net)} × (1 + ${waste}%)`, value: total, unit: 'm²' });
  const kpm = kgPerM2(thickness, material.densityKgM3);
  steps.push({ key: 'kg_m2', label: 'Khối lượng / m²', formula: `${thickness} mm × ${material.densityKgM3} kg/m³ / 1000`, value: kpm, unit: 'kg/m²' });
  const weight = total * kpm;
  steps.push({ key: 'weight', label: 'Khối lượng', formula: `${fmt(total)} m² × ${fmt(kpm)} kg/m²`, value: weight, unit: 'kg' });
  return {
    calcType: def.type,
    label: def.label,
    method: def.method,
    accuracy: def.type === 'round_elbow' && (d.gores ?? 0) > 0 ? 'APPROXIMATION' : def.type === 'round_reducer' && d.e ? 'NUMERIC' : def.accuracy,
    formula: def.formula,
    basis: def.basis,
    dimensionsMm: d,
    dimensionText: dimensionText(def.type, d),
    pieces,
    netAreaPerPieceM2: perPiece,
    netAreaM2: net,
    wastePercent: waste,
    wasteAreaM2: wasteArea,
    totalAreaM2: total,
    faceAreaPerPieceM2: null,
    materialCode: material.code,
    materialName: material.name,
    densityKgM3: material.densityKgM3,
    thicknessMm: thickness,
    kgPerM2: kpm,
    kgPerPiece: weight / pieces,
    totalWeightKg: weight,
    steps,
    warnings,
  };
}

// ---------------------------------------------------------------------------
// PRICING ENGINE (tách khỏi calculation): chọn bán theo m², kg hoặc cái
// ---------------------------------------------------------------------------

export interface DuctPricing {
  pricingUnit: DuctPricingUnit;
  /** SL tính tiền theo đơn vị bán (m² vật tư, kg hoặc cái) – đủ số lẻ */
  pricingQuantity: number;
  unitPrice: number;
  /** Thành tiền = SL tính tiền × đơn giá, làm tròn 0 số lẻ */
  amount: number;
}

/** Đơn vị bán dùng được: ống (hình học) m²/kg; thành phẩm m² mặt/kg/cái. */
export function ductPricingUnits(r: Pick<DuctCalcResult, 'method'>): DuctPricingUnit[] {
  return r.method === 'GEOMETRY' ? ['M2', 'KG'] : ['PIECE', 'M2', 'KG'];
}

export function ductPricingQuantity(r: DuctCalcResult, unit: DuctPricingUnit): number {
  if (!ductPricingUnits(r).includes(unit)) throw new DuctworkError(`Không bán theo ${DUCT_PRICING_UNIT_LABELS[unit]} cho loại này`, 'pricingUnit');
  if (unit === 'KG') return r.totalWeightKg!;
  if (unit === 'PIECE') return r.pieces;
  return r.method === 'GEOMETRY' ? r.totalAreaM2! : r.faceAreaPerPieceM2! * r.pieces;
}

export function priceDuctwork(r: DuctCalcResult, unit: DuctPricingUnit, unitPrice: number): DuctPricing {
  if (!Number.isFinite(unitPrice) || unitPrice < 0) throw new DuctworkError('Đơn giá không hợp lệ', 'unitPrice');
  const q = ductPricingQuantity(r, unit);
  return { pricingUnit: unit, pricingQuantity: q, unitPrice, amount: roundTo(q * unitPrice, 0) };
}

/** Đầu vào bộ tính gửi lên API (POST /ductwork/calculate, trường `ductwork` của dòng báo giá). */
export interface DuctworkRequest {
  calcType: DuctCalcType;
  dimensions: DuctCalcInput['dimensions'];
  pieces: number;
  /** null/bỏ trống = hao hụt mặc định của loại (bảng cấu hình) */
  wastePercent?: number | null;
  materialCode?: string | null;
  thicknessMm?: number | null;
  pricingUnit?: DuctPricingUnit | null;
  unitPrice?: number | null;
  /** ĐVT trên báo giá; bỏ trống = m² (dòng lập trước 08/10/2026) */
  lineUnit?: DuctLineUnit | null;
  /** Lớp tấm chống cháy EI (ống EI) – không đổi giá/SL dòng */
  ei?: EiLayerInput | null;
}

/** Ảnh chụp lưu ở quote_items.ductwork: đầu vào + vật liệu lúc lập + kết quả từng bước + giá. */
export interface DuctworkSnapshot {
  version: 1;
  input: DuctworkRequest & { wasteDefault?: boolean };
  material: DuctMaterial | null;
  result: DuctCalcResult;
  rounded: ReturnType<typeof ductRounded>;
  pricing: DuctPricing | null;
  /** Lớp EI đã tính (ống EI) */
  ei?: EiLayerResult | null;
  calculatedAt: string;
}

/** Làm tròn ở đầu ra: m² 3 số lẻ, kg 2 số lẻ. */
export function ductRounded(r: DuctCalcResult) {
  const a = (v: number | null) => (v === null ? null : roundTo(v, DUCT_AREA_DP));
  const w = (v: number | null) => (v === null ? null : roundTo(v, DUCT_WEIGHT_DP));
  return {
    netAreaM2: a(r.netAreaM2),
    wasteAreaM2: a(r.wasteAreaM2),
    totalAreaM2: a(r.totalAreaM2),
    faceAreaM2: r.faceAreaPerPieceM2 === null ? null : a(r.faceAreaPerPieceM2 * r.pieces),
    kgPerM2: r.kgPerM2 === null ? null : roundTo(r.kgPerM2, 4),
    totalWeightKg: w(r.totalWeightKg),
  };
}

/**
 * Dòng báo giá (DEC-143 mặc định A: dòng lưu theo ĐVT của mã trên Asia).
 * - Ống (mã ĐVT m²): SL = m² vật tư (3 số lẻ); bán theo kg → đơn giá m² = giá kg × kg/m² (2 số lẻ).
 * - Thành phẩm (mã ĐVT cái): SL = số cái; bán theo kg → đơn giá cái = giá kg × kg/cái; theo m² mặt → giá m² × m² mặt/cái.
 * Trả null khi mã không có ĐVT phù hợp (ĐVT m² cho ống).
 */
export interface DuctQuoteLine {
  unit: string;
  quantity: number;
  unitPrice: number;
  weightKgPerUnit: number;
  /** ĐVT dòng khác m²: quy về ĐVT m² của mã (1 m² = perM2 × ĐVT dòng), SL m² = areaM2 */
  toM2?: { m2Unit: string; perM2: number; areaM2: number } | null;
}

export function ductQuoteLine(r: DuctCalcResult, pricingUnit: DuctPricingUnit, price: number, productUnits: string[], lineUnit: DuctLineUnit | null = null): DuctQuoteLine | null {
  if (!ductPricingUnits(r).includes(pricingUnit)) throw new DuctworkError(`Không bán theo ${DUCT_PRICING_UNIT_LABELS[pricingUnit]} cho loại này`, 'pricingUnit');
  if (r.method === 'GEOMETRY') {
    const unit = productUnits.find((u) => isM2Unit(u));
    if (!unit) return null;
    const unitPrice = pricingUnit === 'KG' ? roundTo(price * r.kgPerM2!, 2) : roundTo(price, 2);
    const area = roundTo(r.totalAreaM2!, DUCT_AREA_DP);
    const lu = lineUnit ?? 'M2';
    if (lu === 'M2') return { unit, quantity: area, unitPrice, weightKgPerUnit: roundTo(r.kgPerM2!, 3) };
    if (!ductLineUnits(r).includes(lu)) throw new DuctworkError(`Loại này không bán theo ${DUCT_LINE_UNIT_LABELS[lu]}`, 'lineUnit');
    if (!(area > 0)) throw new DuctworkError('Diện tích quá nhỏ', 'lineUnit');
    const quantity = lu === 'METER' ? roundTo(((r.dimensionsMm.L ?? 0) / 1000) * r.pieces, DUCT_AREA_DP) : r.pieces;
    if (!(quantity > 0)) throw new DuctworkError('Nhập chiều dài ống để bán theo mét', 'lineUnit');
    const label = DUCT_LINE_UNIT_LABELS[lu];
    const named = productUnits.find((u) => u.trim().toLowerCase() === label.toLowerCase()) ?? label;
    return {
      unit: named,
      quantity,
      unitPrice: roundTo((area * unitPrice) / quantity, 2),
      weightKgPerUnit: roundTo((r.kgPerM2! * area) / quantity, 3),
      toM2: { m2Unit: unit, perM2: roundTo(quantity / area, 6), areaM2: area },
    };
  }
  // Thành phẩm: ĐVT chính (cái/bộ); không nhận ĐVT kg/m² làm ĐVT chính
  const unit = productUnits[0];
  if (!unit || isKgUnit(unit) || isM2Unit(unit)) return null;
  const per = pricingUnit === 'KG' ? price * r.kgPerPiece! : pricingUnit === 'M2' ? price * r.faceAreaPerPieceM2! : price;
  return { unit, quantity: r.pieces, unitPrice: roundTo(per, 2), weightKgPerUnit: roundTo(r.kgPerPiece!, 3) };
}

/** Ghi chú in trên báo giá: "Co tròn D500 R750 90° × 4 cái · 0,75 mm GI · hao hụt 15% · 12,54 kg · 35.000 đ/kg". */
export function ductNote(
  r: { label: string; dimensionText: string; pieces: number; thicknessMm: number | null; materialCode: string | null; wastePercent: number; totalWeightKg: number | null; totalAreaM2: number | null; method: DuctMethod },
  pricing?: { pricingUnit: DuctPricingUnit; unitPrice: number; lineUnit?: DuctLineUnit | null } | null,
  ei?: Pick<EiLayerResult, 'board' | 'layers' | 'totalAreaM2' | 'sheets'> | null,
): string {
  const vn = (v: number, dp: number) => v.toLocaleString('vi-VN', { maximumFractionDigits: dp });
  const parts = [`${r.label} ${r.dimensionText} × ${r.pieces} cái`];
  if (r.thicknessMm) parts.push(`${vn(r.thicknessMm, 2)} mm${r.materialCode ? ` ${r.materialCode}` : ''}`);
  if (r.method === 'GEOMETRY') {
    parts.push(`hao hụt ${vn(r.wastePercent, 2)}%`);
    if (r.totalAreaM2 !== null) parts.push(`${vn(r.totalAreaM2, 3)} m²`);
  }
  if (r.totalWeightKg !== null) parts.push(`${vn(r.totalWeightKg, 2)} kg`);
  if (ei) parts.push(`${ei.board.rating} ${ei.board.name} × ${ei.layers} lớp: ${vn(ei.totalAreaM2, 3)} m², ${ei.sheets} tấm ${EI_SHEET.widthMm}×${EI_SHEET.lengthMm}`);
  // Đơn vị bán khác ĐVT dòng (kg; m² mặt của thành phẩm) thì ghi đơn giá gốc
  const lineNotM2 = !!pricing?.lineUnit && pricing.lineUnit !== 'M2';
  if (pricing && (pricing.pricingUnit === 'KG' || (pricing.pricingUnit === 'M2' && (r.method !== 'GEOMETRY' || lineNotM2)))) parts.push(`${vn(pricing.unitPrice, 2)} đ/${DUCT_PRICING_UNIT_LABELS[pricing.pricingUnit]}`);
  return parts.join(' · ');
}

// ---------------------------------------------------------------------------
// LỚP CHỐNG CHÁY EI (anh Tiến 07/10/2026): lớp EI tính theo m² ống, tấm chuẩn 1200×2400;
// tấm thạch cao 9,5 mm và MGO 5 mm đều EI30. Dòng vẫn bán theo m² ống (mã OGEI… ĐVT M2).
// ---------------------------------------------------------------------------

export const EI_SHEET = { widthMm: 1200, lengthMm: 2400 } as const;
/** 1,2 × 2,4 = 2,88 m²/tấm */
export const EI_SHEET_AREA_M2 = (EI_SHEET.widthMm / 1000) * (EI_SHEET.lengthMm / 1000);
export const EI_DEFAULT_WASTE_PERCENT = 10;

export interface EiBoard {
  code: string;
  name: string;
  thicknessMm: number;
  /** Cấp chống cháy một lớp tấm đạt được */
  rating: string;
}

export const DEFAULT_EI_BOARDS: EiBoard[] = [
  { code: 'GYP95', name: 'Thạch cao 9,5 mm', thicknessMm: 9.5, rating: 'EI30' },
  { code: 'MGO5', name: 'MGO 5 mm', thicknessMm: 5, rating: 'EI30' },
];

export interface EiLayerInput {
  boardCode: string;
  /** Số lớp tấm (mặc định 1) */
  layers?: number | null;
  /** Hao hụt cắt tấm (%), bỏ trống = 10% */
  wastePercent?: number | null;
}

export interface EiLayerResult {
  board: EiBoard;
  layers: number;
  /** m² ống thực (chưa hao hụt tôn), mọi cái */
  ductAreaM2: number;
  /** m² tấm EI = m² ống × số lớp */
  eiAreaM2: number;
  wastePercent: number;
  wasteAreaM2: number;
  totalAreaM2: number;
  sheetAreaM2: number;
  /** Số tấm chưa làm tròn */
  sheetsExact: number;
  /** Số tấm 1200×2400 (làm tròn lên) */
  sheets: number;
  accuracy: DuctAccuracy;
  formula: string;
  steps: DuctStep[];
}

/**
 * Số tấm theo diện tích (GẦN ĐÚNG: chưa xếp tấm theo từng mặt, chưa cộng độ dày tấm vào chu vi ngoài).
 * m² EI = m² ống thực × số lớp; tổng = m² EI × (1 + hao hụt); số tấm = ⌈tổng / 2,88⌉.
 */
export function calcEiLayer(r: DuctCalcResult, input: EiLayerInput, boards: EiBoard[] = DEFAULT_EI_BOARDS): EiLayerResult {
  if (r.method !== 'GEOMETRY' || r.netAreaM2 === null) throw new DuctworkError('Lớp EI chỉ tính cho ống và phụ kiện ống (theo m²)', 'ei');
  const board = boards.find((b) => b.code === input.boardCode);
  if (!board) throw new DuctworkError('Chọn loại tấm EI', 'ei.boardCode');
  const layers = input.layers == null ? 1 : Number(input.layers);
  if (!Number.isInteger(layers) || layers < 1 || layers > 5) throw new DuctworkError('Số lớp tấm EI từ 1 đến 5', 'ei.layers');
  const waste = input.wastePercent == null ? EI_DEFAULT_WASTE_PERCENT : Number(input.wastePercent);
  if (!Number.isFinite(waste) || waste < 0 || waste > 100) throw new DuctworkError('Hao hụt tấm EI phải từ 0 đến 100%', 'ei.wastePercent');
  const steps: DuctStep[] = [];
  const eiArea = r.netAreaM2 * layers;
  steps.push({ key: 'ei_area', label: 'Diện tích tấm EI', formula: `${fmt(r.netAreaM2)} m² ống × ${layers} lớp`, value: eiArea, unit: 'm²' });
  const wasteArea = (eiArea * waste) / 100;
  steps.push({ key: 'ei_waste', label: 'Hao hụt cắt tấm', formula: `${fmt(eiArea)} × ${waste}%`, value: wasteArea, unit: 'm²' });
  const total = eiArea + wasteArea;
  steps.push({ key: 'ei_total', label: 'Tổng diện tích tấm EI', formula: `${fmt(eiArea)} × (1 + ${waste}%)`, value: total, unit: 'm²' });
  const exact = total / EI_SHEET_AREA_M2;
  const sheets = Math.ceil(exact - 1e-9);
  steps.push({ key: 'ei_sheets', label: `Số tấm ${EI_SHEET.widthMm}×${EI_SHEET.lengthMm}`, formula: `⌈${fmt(total)} / ${fmt(EI_SHEET_AREA_M2)}⌉ (= ${fmt(exact)})`, value: sheets, unit: 'tấm' });
  return {
    board,
    layers,
    ductAreaM2: r.netAreaM2,
    eiAreaM2: eiArea,
    wastePercent: waste,
    wasteAreaM2: wasteArea,
    totalAreaM2: total,
    sheetAreaM2: EI_SHEET_AREA_M2,
    sheetsExact: exact,
    sheets,
    accuracy: 'APPROXIMATION',
    formula: `Số tấm = ⌈m² ống × số lớp × (1 + hao hụt) / ${fmt(EI_SHEET_AREA_M2)}⌉`,
    steps,
  };
}
